// CSV export for the calculation engine.
//
// Views:
//   - `formatItemResultsCsv(result)` — wide table, one row per item
//     with all factor-chain columns side-by-side.
//   - `formatFactorChainCsv(result)` — long-form, one row per
//     (item, factor) pair.
//   - `formatChangePremiumCsv(result)` — change-premium breakdown:
//     one row per item with before / after / difference / premium.
//   - `formatBatchCsv(cases)` — batch summary: one row per case with
//     land price / premium / status.
//
// All numeric outputs are written without thousands separators and
// with up to 6 fractional digits so downstream tools can round as
// needed.

import type { ChangePremiumResult } from "../types/change-premium.js";
import type { CalculationResult } from "../types/calculation-result.js";
import type { BatchCaseOutput } from "./batch.js";

interface FactorRow {
  readonly itemId: string;
  readonly factor: string;
  readonly value: number;
  readonly notes: string;
}

/**
 * Wide-form CSV: one row per item, factor-chain columns side by side.
 *
 * Columns: itemId, buildingType, gfa, chargeableGfa, applicablePriceType,
 * marketPrice, buildingTypeFactor, landTermFactor, propertyConditionFactor,
 * affordableHousingFactor, industryOrientationFactor,
 * industryProjectTypeFactor, commercialFloorFactor, undergroundSpaceFactor,
 * projectFactor, finalFactor, itemLandPrice, floorLandPrice, formulaText.
 */
export function formatItemResultsCsv(result: CalculationResult): string {
  const headers = [
    "itemId",
    "buildingType",
    "gfa",
    "chargeableGfa",
    "applicablePriceType",
    "marketPrice",
    "buildingTypeFactor",
    "landTermFactor",
    "propertyConditionFactor",
    "affordableHousingFactor",
    "industryOrientationFactor",
    "industryProjectTypeFactor",
    "commercialFloorFactor",
    "undergroundSpaceFactor",
    "projectFactor",
    "finalFactor",
    "itemLandPrice",
    "floorLandPrice",
    "formulaText",
  ];
  const lines = [headers.join(",")];
  for (const r of result.itemResults) {
    const c = r.factorChain;
    const row = [
      r.itemId,
      r.buildingType,
      r.gfa,
      r.chargeableGfa,
      r.applicablePriceType,
      r.marketPrice,
      c.buildingTypeFactor,
      c.landTermFactor,
      c.propertyConditionFactor,
      c.affordableHousingFactor,
      c.industryOrientationFactor,
      c.industryProjectTypeFactor,
      c.commercialFloorFactor,
      c.undergroundSpaceFactor,
      c.projectFactor,
      c.finalFactor,
      r.itemLandPrice,
      r.floorLandPrice,
      r.formulaText,
    ];
    lines.push(row.map(csvEscape).join(","));
  }
  return lines.join("\n") + "\n";
}

/**
 * Long-form CSV: one row per (item, factor) pair. Factor names match
 * the keys used in `FactorChain`.
 */
export function formatFactorChainCsv(result: CalculationResult): string {
  const lines = ["itemId,buildingType,factor,value,notes"];
  for (const r of result.itemResults) {
    const c = r.factorChain;
    const notes = c.notes.join("；");
    const factors: FactorRow[] = [
      { itemId: r.itemId, factor: "marketPrice", value: c.marketPrice, notes },
      { itemId: r.itemId, factor: "buildingTypeFactor", value: c.buildingTypeFactor, notes },
      { itemId: r.itemId, factor: "landTermFactor", value: c.landTermFactor, notes },
      { itemId: r.itemId, factor: "propertyConditionFactor", value: c.propertyConditionFactor, notes },
      { itemId: r.itemId, factor: "affordableHousingFactor", value: c.affordableHousingFactor, notes },
      { itemId: r.itemId, factor: "industryOrientationFactor", value: c.industryOrientationFactor, notes },
      { itemId: r.itemId, factor: "industryProjectTypeFactor", value: c.industryProjectTypeFactor, notes },
      { itemId: r.itemId, factor: "commercialFloorFactor", value: c.commercialFloorFactor, notes },
      { itemId: r.itemId, factor: "undergroundSpaceFactor", value: c.undergroundSpaceFactor, notes },
      { itemId: r.itemId, factor: "projectFactor", value: c.projectFactor, notes },
      { itemId: r.itemId, factor: "finalFactor", value: c.finalFactor, notes },
    ];
    for (const f of factors) {
      lines.push(
        [f.itemId, r.buildingType, f.factor, f.value, f.notes]
          .map(csvEscape)
          .join(","),
      );
    }
  }
  return lines.join("\n") + "\n";
}

/**
 * Change-premium CSV: one row per (union of before / after) item with
 * before / after land prices, the per-item difference (clamped to 0
 * for the premium), and the case-level totals.
 *
 * Items that exist in only one side still appear once, with the
 * missing side blanked out.
 */
export function formatChangePremiumCsv(result: ChangePremiumResult): string {
  const headers = [
    "itemId",
    "buildingType",
    "beforeGfa",
    "afterGfa",
    "beforeItemLandPrice",
    "afterItemLandPrice",
    "itemPremium",
  ];
  const lines = [headers.join(",")];

  // Index both sides by itemId.
  const beforeById = new Map(
    result.beforeResult.itemResults.map((r) => [
      r.itemId,
      { buildingType: r.buildingType, gfa: r.gfa, itemLandPrice: r.itemLandPrice },
    ]),
  );
  const afterById = new Map(
    result.afterResult.itemResults.map((r) => [
      r.itemId,
      { buildingType: r.buildingType, gfa: r.gfa, itemLandPrice: r.itemLandPrice },
    ]),
  );

  const ids = new Set<string>([...beforeById.keys(), ...afterById.keys()]);
  for (const id of ids) {
    const b = beforeById.get(id);
    const a = afterById.get(id);
    const beforePrice = b?.itemLandPrice ?? 0;
    const afterPrice = a?.itemLandPrice ?? 0;
    const itemPremium = Math.max(afterPrice - beforePrice, 0);
    lines.push(
      [
        id,
        a?.buildingType ?? b?.buildingType ?? "",
        b?.gfa ?? "",
        a?.gfa ?? "",
        b ? beforePrice : "",
        a ? afterPrice : "",
        itemPremium,
      ]
        .map(csvEscape)
        .join(","),
    );
  }

  // Total row.
  lines.push(
    [
      "TOTAL",
      "",
      "",
      "",
      result.beforeResult.totalLandPrice,
      result.afterResult.totalLandPrice,
      result.premium,
    ]
      .map(csvEscape)
      .join(","),
  );
  return lines.join("\n") + "\n";
}

/**
 * Batch CSV: one row per case with the case's land price, premium,
 * status, and validation message count.
 */
export function formatBatchCsv(cases: BatchCaseOutput[]): string {
  const headers = [
    "index",
    "title",
    "calculationType",
    "landPrice",
    "premium",
    "validationMessageCount",
    "status",
  ];
  const lines = [headers.join(",")];
  for (const c of cases) {
    let landPrice = 0;
    let premium = 0;
    let calcType = "校验失败";
    if (c.calculationType === "new-supply" && c.result) {
      const r = c.result as CalculationResult;
      landPrice = r.totalLandPrice;
      calcType = "新供应土地";
    } else if (c.calculationType === "change-premium" && c.result) {
      const cp = c.result as ChangePremiumResult;
      landPrice = cp.afterResult.totalLandPrice;
      premium = cp.premium;
      calcType = "变更补缴";
    }
    const status = c.calculationType === "unknown" ? "失败" : "通过";
    lines.push(
      [
        c.index + 1,
        c.title ?? `case-${c.index + 1}`,
        calcType,
        landPrice,
        premium,
        c.validationMessages.length,
        status,
      ]
        .map(csvEscape)
        .join(","),
    );
  }
  return lines.join("\n") + "\n";
}

function csvEscape(v: unknown): string {
  if (v === undefined || v === null) return "";
  const s = typeof v === "number" ? formatNumber(v) : String(v);
  if (/[",\n\r]/.test(s)) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

function formatNumber(n: number): string {
  if (!Number.isFinite(n)) return String(n);
  // Round to 6 decimal places.
  const rounded = Math.round(n * 1_000_000) / 1_000_000;
  return String(rounded);
}